<template>
  <div class="top-table" @click="onTableClick">
    <div class="top-table-header">
      <div class="top-table-title">{{ props.title }}</div>
      <Dropdown
        :items="props.metrics"
        :default_value="props.metrics.findIndex(m => m.key === props.active_sort)"
        :direction_type="'right'"
        :icon_direction_type="'right'"
        @update-dropdown="$emit('sort-change', $event.key)"
      />
    </div>
    <div class="top-table-body">
      <Loading class="small" v-if="props.loading" />
      <template v-else>
        <div
          class="top-table-row"
          v-for="(item, index) in props.items"
          :key="item.id || item.advertiser_id || index"
          @click="$emit('row-click', item)"
        >
          <div class="row-info" :class="{ 'brand-only': props.type === 'brand' || props.type === 'advertiser' }">
            <template v-if="props.type === 'creator'">
              <div
                class="avatar"
                :style="item.userProfile?.avatar_full_url
                  ? { backgroundImage: `url(${item.userProfile.avatar_full_url})`, backgroundSize: 'cover' }
                  : { background: '#C8D8F0' }"
              >
                <template v-if="!item.userProfile?.avatar_full_url">
                  {{ (item.first_name?.[0] || '') + (item.last_name?.[0] || '') }}
                </template>
              </div>
              <div class="row-name">
                <div class="name">{{ item.first_name }} {{ item.last_name }}</div>
                <div class="username">{{ item.motom_handle ? '@' + item.motom_handle : item.instagram_handle ? '@' + item.instagram_handle : item.tiktok_handle || '' }}</div>
              </div>
            </template>
            <template v-else-if="props.type === 'product'">
              <div
                class="product-img"
                :style="item.product_image
                  ? { backgroundImage: `url(${item.product_image})`, backgroundSize: 'cover' }
                  : { background: '#E0E0E0' }"
              ></div>
              <div class="row-name">
                <div class="name">{{ item.product_name }}</div>
                <div class="username">{{ item.brand_name }}</div>
              </div>
            </template>
            <template v-else>
              <div class="name">{{ item.display_name || item.name }}</div>
            </template>
          </div>
          <div class="row-single-value">{{ topValue(item) }}</div>
        </div>
        <NoData
          v-if="props.items.length === 0"
          :image="require('@/assets/images/cart.png')"
          text=""
          title="No data available"
        />
      </template>
    </div>
  </div>
</template>

<script setup>
import { getCurrentInstance } from 'vue';
import Loading from '@/components/ui/Loading.vue';
import NoData from '@/components/creator/NoData.vue';
import Dropdown from '@/components/ui/Dropdown.vue';

const { proxy } = getCurrentInstance();
const onTableClick = () => proxy.EventBus.emit('close-dropdown-tags', '');

const props = defineProps({
  title: { type: String, required: true },
  metrics: { type: Array, required: true },
  items: { type: Array, default: () => [] },
  active_sort: { type: String, required: true },
  type: { type: String, default: 'brand' },
  loading: { type: Boolean, default: false },
});

defineEmits(['sort-change', 'row-click']);

const topValue = (item) => {
  const sortKey = props.active_sort;
  const isMoney = sortKey === 'sales_value' || sortKey === 'earnings';
  const val = item[sortKey];
  if (isMoney) return typeof val === 'number' ? '$' + Math.ceil(val).toLocaleString() : '---';
  return val ? Number(val).toLocaleString() : '---';
};
</script>

<style lang="scss">
.top-table {
  border: solid 1px var(--border);
  border-radius: 8px;
  padding:16px 0;

  .top-table-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 4px;
    padding: 0 16px;

    .top-table-title {
      font: var(--font-demi-20);
      color: var(--black-90);
    }

  }

  .top-table-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    cursor: pointer;
    &:hover {
      background: var(--bg-08);
    }

    .row-info {
      display: flex;
      align-items: center;
      gap: 8px;
      flex: 1;
      min-width: 0;

      &.brand-only .name {
        font-size: 12px;
        font-weight: 500;
        color: var(--black-90);
      }

      .avatar {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 9px;
        font-weight: 600;
        color: var(--black-60);
        flex-shrink: 0;
        overflow: hidden;
      }

      .product-img {
        width: 28px;
        height: 28px;
        border-radius: 4px;
        flex-shrink: 0;
      }

      .row-name {
        min-width: 0;

        .name {
          font:var(--font-med-14);
          color: var(--black-90);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .username { font-size: 12px; color: var(--black-50); }
      }
    }

    .row-single-value {
      font-size: 12px;
      font-weight: 500;
      color: var(--black-90);
      flex-shrink: 0;
      text-align: right;
    }
  }
}
</style>
